import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { TreeGridComponent, ColumnsDirective, ColumnDirective } from '@syncfusion/ej2-react-treegrid';
import { RowDataBoundEventArgs, RowSelectingEventArgs } from '@syncfusion/ej2-react-grids';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import * as Inovx from '@shared';
import ContextMenu from '@facility/viewers/components/ContextMenu';

var UrlUtilities = Inovx.Shared.Common.UrlUtilities;
var InovxControls = Inovx.Shared.Controls;

declare var InovxLoader: any;

interface AssetSetTaxonomyTreeProps {
    taxonomy: ILibraryTaxonomyDetail | null;
    onChange: (taxonomy: ILibraryTaxonomyDetail) => void;
    resetToken: number;
}

interface TreeNode extends IClassificationTag {
    ClassificationTags?: TreeNode[];
    isExpanded?: boolean;
    IsTaxonomyRoot?: boolean;
}

interface MenuState {
    visible: boolean;
    left: number;
    top: number;
    node: TreeNode | null;
    target: 'taxonomy' | 'tag';
}

type TagDialogMode = 'add' | 'edit';

function buildTagTree(data: IClassificationTag[], expandedIds: string[]): TreeNode[] {
    const map = new Map<string, TreeNode>();
    const roots: TreeNode[] = [];
    const sorted = [...data].sort((a, b) => a.Name.localeCompare(b.Name, undefined, { sensitivity: 'base' }));

    sorted.forEach((item) => {
        map.set(item.ID, {
            ...item,
            ClassificationTags: [],
            isExpanded: expandedIds.includes(item.ID),
        });
    });

    sorted.forEach((item) => {
        const node = map.get(item.ID)!;
        if (item.ParentID) {
            const parent = map.get(item.ParentID);
            if (parent) {
                parent.ClassificationTags!.push(node);
            }
        } else {
            roots.push(node);
        }
    });

    return roots;
}

function buildTree(taxonomy: ILibraryTaxonomyDetail | null, expandedIds: string[]): TreeNode[] {
    if (!taxonomy) {
        return [];
    }

    return [{
        ID: taxonomy.ID,
        Name: InovxLoader.t('facility:form.assetSetTaxonomyFixedName'),
        ParentID: null,
        VersionToken: taxonomy.VersionToken,
        ClassificationTags: buildTagTree(taxonomy.ClassificationTags || [], expandedIds),
        isExpanded: true,
        IsTaxonomyRoot: true
    }];
}

const AssetSetTaxonomyTree: React.FC<AssetSetTaxonomyTreeProps> = ({ taxonomy, onChange, resetToken }) => {
    const treeGridRef = useRef<TreeGridComponent>(null);
    const [selectedNode, setSelectedNode] = useState<TreeNode | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [expandedRows, setExpandedRows] = useState<string[]>([]);
    const [menu, setMenu] = useState<MenuState>({ visible: false, left: -1000, top: -1000, node: null, target: 'taxonomy' });
    const [isTagDialogOpen, setIsTagDialogOpen] = useState(false);
    const [tagDialogMode, setTagDialogMode] = useState<TagDialogMode>('add');
    const [isCreatingRootTag, setIsCreatingRootTag] = useState(false);
    const [editingNode, setEditingNode] = useState<TreeNode | null>(null);
    const [newTagName, setNewTagName] = useState('');
    const [hasTagNameError, setHasTagNameError] = useState(false);
    const newlyCreatedIdRef = useRef<string | null>(null);

    const treeDataSource = useMemo(() => buildTree(taxonomy, expandedRows), [expandedRows, taxonomy]);
    const treeGridKey = `schema-asset-set-taxonomy-${taxonomy?.ID || 'empty'}`;

    const resetTreeState = () => {
        setSelectedNode(null);
        setSelectedId(null);
        setExpandedRows([]);
        setMenu({ visible: false, left: -1000, top: -1000, node: null, target: 'taxonomy' });
        setIsTagDialogOpen(false);
        setTagDialogMode('add');
        setIsCreatingRootTag(false);
        setEditingNode(null);
        setNewTagName('');
        setHasTagNameError(false);
        newlyCreatedIdRef.current = null;
    };

    useEffect(() => {
        resetTreeState();
    }, [resetToken]);

    useEffect(() => {
        if (selectedId === taxonomy?.ID) {
            return;
        }

        if (selectedId && !(taxonomy?.ClassificationTags || []).some((tag) => tag.ID === selectedId)) {
            setSelectedId(null);
            setSelectedNode(null);
        }
    }, [selectedId, taxonomy]);

    function findNodeById(nodes: TreeNode[], id: string): TreeNode | null {
        for (const node of nodes) {
            if (node.ID === id) {
                return node;
            }
            if (node.ClassificationTags?.length) {
                const found = findNodeById(node.ClassificationTags, id);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    }

    function getSiblingNames(parentId: string | null, excludeId?: string): string[] {
        return (taxonomy?.ClassificationTags || [])
            .filter((tag) => (tag.ParentID || null) === parentId && tag.ID !== excludeId)
            .map((tag) => tag.Name.toLowerCase());
    }

    function collectIdsToDelete(node: TreeNode): string[] {
        const ids = [node.ID];
        if (node.ClassificationTags?.length) {
            for (const child of node.ClassificationTags) {
                ids.push(...collectIdsToDelete(child));
            }
        }
        return ids;
    }

    const updateTaxonomy = (classificationTags: IClassificationTag[]) => {
        if (!taxonomy) {
            return;
        }
        onChange({
            ...structuredClone(taxonomy),
            Name: 'Asset Set Taxonomy',
            Type: TaxonomyType.AssetSet,
            ClassificationTags: classificationTags
        });
    };

    const openContextMenu = (event: React.MouseEvent, target: 'taxonomy' | 'tag', node: TreeNode | null = null) => {
        event.preventDefault();
        event.stopPropagation();
        setMenu({ visible: true, left: event.clientX, top: event.clientY, node, target });
    };

    const hideContextMenu = () => setMenu({ visible: false, left: -1000, top: -1000, node: null, target: 'taxonomy' });

    const getMenuItems = () => {
        if (menu.target === 'taxonomy') {
            return [{ id: 'add-root', label: InovxLoader.t('facility:form.addRootTag') }];
        }

        if (!menu.node?.ParentID) {
            return [
                { id: 'add-child', label: InovxLoader.t('facility:form.addChildTag') },
                { id: 'edit', label: InovxLoader.t('facility:form.editRootTag') },
                { id: 'delete', label: InovxLoader.t('facility:form.deleteRootTag') }
            ];
        }

        return [
            { id: 'add-child', label: InovxLoader.t('facility:form.addChildTag') },
            { id: 'edit', label: InovxLoader.t('facility:form.editChildTag') },
            { id: 'delete', label: InovxLoader.t('facility:form.deleteTag') }
        ];
    };

    const handleMenuItemClick = (itemId: string) => {
        const node = menu.node;
        hideContextMenu();
        if (itemId === 'add-root') {
            openAddTagForm(true);
            return;
        }
        if (!node) {
            return;
        }
        setSelectedId(node.ID);
        setSelectedNode(node);
        if (itemId === 'add-child') {
            openAddTagForm(false, node);
        }
        if (itemId === 'edit') {
            openEditTagForm(node);
        }
        if (itemId === 'delete') {
            deleteTag(node);
        }
    };

    const handleRowSelecting = (_e: RowSelectingEventArgs) => {};

    const handleRowSelected = (e: any) => {
        const id = e?.data?.ID;
        if (!id) {
            return;
        }
        setSelectedId(id);
        const selected = findNodeById(treeDataSource, id);
        setSelectedNode(selected?.IsTaxonomyRoot ? null : selected);
    };

    const handleDataBound = () => {
        const grid = treeGridRef.current;
        if (!grid) {
            return;
        }

        const targetId = newlyCreatedIdRef.current || selectedId;
        if (!targetId) {
            return;
        }

        const records = grid.getCurrentViewRecords() as any[];
        const rowIndex = records.findIndex((row) => row.ID === targetId);
        if (rowIndex >= 0) {
            grid.selectRow(rowIndex);
            setSelectedId(targetId);
        }
        newlyCreatedIdRef.current = null;
    };

    const handleQueryCellInfo = (args: any) => {
        if (args.column.field !== 'Name') {
            return;
        }

        const cell = args.cell as HTMLElement;
        const treeCell = cell.querySelector('.e-treecell') as HTMLElement;
        if (!treeCell || treeCell.querySelector('.facility-hierarchy-treegrid-name')) {
            return;
        }

        const text = treeCell.textContent?.trim() ?? '';
        const labelClass = !args?.data?.ParentID ? 'facility-hierarchy-treegrid-name-label parent-name' : 'facility-hierarchy-treegrid-name-label';
        const iconName = args?.data?.IsTaxonomyRoot ? 'schema/Schema.png' : 'icons/tag.png';
        treeCell.innerHTML = `<div class="facility-hierarchy-treegrid-name"><div class="facility-hierarchy-treegrid-name-img"><img class="e-treeoverviewimage" src="${UrlUtilities.getBaseApplicationUrl()}/images/${iconName}" /></div><div class="${labelClass}">${text}</div></div>`;
    };

    const updateExpandedStatus = (args: any) => {
        const id = args?.data?.ID;
        if (!id || args?.data?.IsTaxonomyRoot) {
            return;
        }

        setExpandedRows((prev) => {
            if (args.name === 'expanded') {
                return prev.includes(id) ? prev : [...prev, id];
            }
            if (args.name === 'collapsed') {
                return prev.filter((item) => item !== id);
            }
            return prev;
        });
    };

    const openAddTagForm = (isRoot: boolean, node: TreeNode | null = null) => {
        setTagDialogMode('add');
        setIsCreatingRootTag(isRoot);
        setEditingNode(null);
        if (node) {
            setSelectedNode(node);
            setSelectedId(node.ID);
        }
        setNewTagName('');
        setHasTagNameError(true);
        setIsTagDialogOpen(true);
    };

    const openEditTagForm = (node: TreeNode) => {
        setTagDialogMode('edit');
        setIsCreatingRootTag(!node.ParentID);
        setEditingNode(node);
        setSelectedNode(node);
        setSelectedId(node.ID);
        setNewTagName(node.Name);
        setHasTagNameError(false);
        setIsTagDialogOpen(true);
    };

    const handleAddTagInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setNewTagName(value);
        const trimmed = value.trim();
        const parentId = tagDialogMode === 'edit'
            ? editingNode?.ParentID ?? null
            : isCreatingRootTag ? null : selectedNode?.ID ?? null;
        const excludeId = tagDialogMode === 'edit' ? editingNode?.ID : undefined;
        setHasTagNameError(!trimmed || getSiblingNames(parentId, excludeId).includes(trimmed.toLowerCase()));
    };

    const saveTagDialog = () => {
        if (!taxonomy || hasTagNameError) {
            return;
        }

        if (tagDialogMode === 'edit') {
            if (!editingNode) {
                return;
            }

            const updatedTags = (taxonomy.ClassificationTags || []).map((tag) =>
                tag.ID === editingNode.ID ? { ...tag, Name: newTagName.trim() } : tag
            );
            updateTaxonomy(updatedTags);
            setIsTagDialogOpen(false);
            return;
        }

        if (!selectedNode && !isCreatingRootTag) {
            return;
        }

        const parentId = isCreatingRootTag ? null : selectedNode?.ID ?? null;
        const newId = crypto.randomUUID();
        const newTag: IClassificationTag = {
            ID: newId,
            Name: newTagName.trim(),
            ParentID: parentId,
            VersionToken: ''
        };

        if (parentId) {
            setExpandedRows((prev) => prev.includes(parentId) ? prev : [...prev, parentId]);
        }
        newlyCreatedIdRef.current = newId;
        updateTaxonomy([...(taxonomy.ClassificationTags || []), newTag]);
        setIsTagDialogOpen(false);
    };

    const closeTagDialog = () => {
        setIsTagDialogOpen(false);
        setEditingNode(null);
    };

    const deleteTag = (node: TreeNode) => {
        const idsToDelete = collectIdsToDelete(node);
        updateTaxonomy((taxonomy?.ClassificationTags || []).filter((tag) => !idsToDelete.includes(tag.ID)));
        if (selectedId && idsToDelete.includes(selectedId)) {
            setSelectedId(null);
            setSelectedNode(null);
        }
    };

    return (
        <div className="schema-asset-set-taxonomy">
            <div className="form-field">
                <label className="control-label">{InovxLoader.t('facility:form.assetSetTaxonomyFixedName')}</label>
            </div>
            <TreeGridComponent
                className="responsive-taxonomy-list"
                ref={treeGridRef}
                key={treeGridKey}
                dataSource={treeDataSource}
                allowSelection
                editSettings={{ allowEditing: false }}
                expandStateMapping="isExpanded"
                expanded={updateExpandedStatus}
                collapsed={updateExpandedStatus}
                idMapping="ID"
                parentIdMapping="ParentID"
                childMapping="ClassificationTags"
                treeColumnIndex={0}
                height="200"
                rowHeight={36}
                selectionSettings={{ mode: 'Row', type: 'Single', enableToggle: false }}
                rowSelecting={handleRowSelecting}
                rowSelected={handleRowSelected}
                dataBound={handleDataBound}
                queryCellInfo={handleQueryCellInfo}
                allowKeyboard={false}
                locale={InovxLoader.getSyncfusionJS2Locale() || undefined}
            >
                <ColumnsDirective>
                    <ColumnDirective field="Name" headerText={InovxLoader.t('facility:header.classification')} allowEditing={false} />
                    <ColumnDirective field="ID" visible={false} isPrimaryKey />
                    <ColumnDirective field="Actions" headerText="" width="40" allowEditing={false} template={(props: TreeNode) => (
                        <button
                            type="button"
                            className="no-btn"
                            onClick={(event) => openContextMenu(event, props.IsTaxonomyRoot ? 'taxonomy' : 'tag', props.IsTaxonomyRoot ? null : props)}
                        >
                            <i className="fa fa-ellipsis-v" />
                        </button>
                    )} />
                </ColumnsDirective>
            </TreeGridComponent>

            {menu.visible && (
                <ContextMenu
                    left={menu.left}
                    top={menu.top}
                    items={getMenuItems()}
                    onItemClick={handleMenuItemClick}
                    onRequestClose={hideContextMenu}
                />
            )}

            {isTagDialogOpen && (
                <DialogComponent
                    target={'#app'}
                    width="350px"
                    showCloseIcon={true}
                    visible={true}
                    isModal={true}
                    zIndex={110000}
                    header={() => <div><div className="e-icon-settings dlg-template">{tagDialogMode === 'edit' ? InovxLoader.t('facility:form.editClassificationTag') : InovxLoader.t('facility:form.addClassificationTag')}</div></div>}
                    footerTemplate={() => (
                        <div>
                            <div className="d-flex j-cc">
                                <div>
                                    <a id="schemaAssetSetTaxonomy-save-btn" onClick={saveTagDialog} className={`btn btn-primary${hasTagNameError ? ' disabled' : ''}`}>{InovxLoader.t('facility:preferences.saveButton')}</a>
                                </div>
                                <div className="ml-1">
                                    <a id="schemaAssetSetTaxonomy-cancel-btn" onClick={closeTagDialog} className="btn btn-default">{InovxLoader.t('facility:preferences.cancelButton')}</a>
                                </div>
                            </div>
                        </div>
                    )}
                    close={closeTagDialog}
                >
                    <div className="dialogContent">
                        <InovxControls.FormField title={InovxLoader.t('facility:form.classificationName')} inputName="Name" className={hasTagNameError ? 'has-error' : ''}>
                            <input type="text" onChange={handleAddTagInputChange} value={newTagName} className="form-control" name="Name" placeholder={InovxLoader.t('facility:form.classificationPlaceholder')} autoComplete="off" />
                        </InovxControls.FormField>
                    </div>
                </DialogComponent>
            )}
        </div>
    );
};

export default AssetSetTaxonomyTree;
